import { type AnyType } from './any';
import { type UUID } from './api/uuid';
import { DimensionType } from './field';
import { type TimeFrames } from './timeFrames';

export const COLUMN_INFO = 'columnInfo';
export const EVENT_TABLE_PREFIX = 'raw_events';
export const DEDUPED_EVENTS_TABLE_PREFIX = 'srtdedupedevents';
export const DEDUPED_SUBSCRIPTION_EVENTS_TABLE_PREFIX = 'subscriptionevents';
export const DEDUPED_JOURNEY_EVENTS_TABLE_PREFIX = 'srtjourneyevents';
export const BOT_DETECTION_TABLE_PREFIX = 'srtbotdetection';
export const CUSTOMER_COMMUNICATION_LOGS_TABLE_PREFIX =
    'customercommunicationlogs';
export const EVENT_LOG_TABLE_PREFIX = 'eventlogs';
export const SUBSCRIPTION_EVENT_NAME = 'SUBSCRIPTION_EVENT';
export const CUSTOMER_COMMUNICATION_LOGS_EVENT_NAME =
    'CUSTOMER_COMMUNICATION_LOG';
export const SOURCE_EVENT_KEY_MAPPINGS_TABLE = 'source_event_key_mappings';
export const AUDIENCE_IN_EVENT = 'AUDIENCE_IN';
export const AUDIENCE_OUT_EVENT = 'AUDIENCE_OUT';
export const JOURNEY_AUDIENCE_SCHEDULED_EVENT = 'JOURNEY_AUDIENCE_SCHEDULED';
export const USER_WH_CACHE_SYNC_EVENT = 'USER_WH_CACHE_SYNC';
export const AI_CLASSIFY_EVENT_NAME = 'AI_CLASSIFY_COMPLETED';

export enum Sources {
    GUPSHUP = 'gupshup',
    TWILIO = 'twilio',
    SENDGRID = 'sendgrid',
    FYNO = 'fyno',
    SRT_INTERNAL = 'srt_internal',
}

export enum ReservedCampaignEventColumns {
    MSG_ID = 'srt_cmp_msg_id',
    STATUS = 'srt_cmp_status',
    CUSTOM_ID = 'srt_cmp_custom_id',
    RECIPENT = 'srt_cmp_recipent',
    PROVIDER_NAME = 'srt_cmp_provider_name',
    MESSAGE = 'srt_cmp_message',
    REQUEST_TIME = 'srt_cmp_request_time',
    DELIVERY_TIME = 'srt_cmp_delivery_time',
    ACCOUNT_NAME = 'srt_cmp_account_name',
    CHANNEL = 'srt_cmp_channel',
    CAMPAIGN_NAME = 'srt_campaign_name',
    PARENT_CAMPAIGN_ID = 'srt_cmp_parent_campaign_id',
    LINK_CLICKED = 'srt_cmp_clicked_link',
    // This also should not change frequently
}

export enum CommonEventColumns {
    CLIENT_EVENT_ID = 'srt_client_event_id',
    CLIENT_TIMESTAMP = 'srt_client_timestamp',
    // Add more common columns here
}

export enum SubscriptionEventColumns {
    CHANNEL_NAME = 'srt_subscription_channel_name',
    SUBSCRIPTION_GROUP_ID = 'srt_subscription_group_id',
    SUBSCRIPTION_GROUP_TYPE = 'srt_subscription_group_type',
    USER_ACTION_TAKEN = 'srt_subscription_user_action_taken',
    SUBSCRIBE_TIMESTAMP = 'srt_subscribe_timestamp',
    UNSUBSCRIBE_TIMESTAMP = 'srt_unsubscribe_timestamp',
}

export enum DeliveryControlEventsColumns {
    DELIVERY_CONTROL_RULE_ID = 'srt_delivery_control_rule_id',
    DELIVERY_CONTROL_RULE_COUNTER = 'srt_delivery_control_rule_counter',
}

export enum ReservedEventColumns {
    SOURCE = 'srt_source',
    EVENT_NAME = 'srt_event_name',
    RAW_PAYLOAD = 'srt_raw_payload',
    EVENT_ID = 'srt_event_id',
    SERVER_TIMESTAMP = 'srt_server_timestamp',
    EVENT_MAPPING_STATUS = 'srt_event_mapping_status',
    MAPPING_ERRORS = 'srt_mapping_errors',
    USER_ID = 'srt_user_id',
    AUDIENCE_ID = 'srt_audience_id',
    AUDIENCE_CONTEXT_ID = 'srt_audience_context_id',
    // This should not be changed frequently
}

export enum JourneyEventColumns {
    JOURNEY_ID = 'srt_journey_id',
    JOURNEY_EXECUTION_ID = 'srt_journey_execution_id',
    JOURNEY_NODE_ID = 'srt_journey_node_id',
    JOURNEY_CONTEXT_ID = 'srt_journey_context_id',
    JOURNEY_VERSION_ID = 'srt_journey_version_id',
    JOURNEY_PARENT_ID = 'srt_journey_parent_id',
    JOURNEY_EVENT_METADATA = 'srt_journey_event_metadata',
}

export enum JourneyEventNames {
    JOURNEY_ENTERED = 'JOURNEY_ENTERED',
    JOURNEY_EXITED = 'JOURNEY_EXITED',
    JOURNEY_CONVERTED = 'JOURNEY_CONVERTED',
    JOUNREY_NODE_ENTERED = 'JOURNEY_NODE_ENTERED',
    JOURNEY_NODE_EXITED = 'JOURNEY_NODE_EXITED',
    JOURNEY_NODE_TIMEDOUT = 'JOURNEY_NODE_TIMEDOUT',
    JOURNEY_NODE_FAILED = 'JOURNEY_NODE_FAILED',
    JOURNEY_EXPERIMENT_CONVERTED = 'JOURNEY_EXPERIMENT_CONVERTED',
    SEND_TO_JOURNEY = 'SEND_TO_JOURNEY',
}

export const SRT_LOAD_TIMESTAMP = 'srt_load_ts';

export type MappedEvent = {
    [ReservedEventColumns.SOURCE]: string;
    [ReservedEventColumns.RAW_PAYLOAD]: string;
    [ReservedEventColumns.EVENT_ID]: string;
    [ReservedEventColumns.EVENT_NAME]: string;
    [ReservedEventColumns.SERVER_TIMESTAMP]: number;
    [ReservedEventColumns.EVENT_MAPPING_STATUS]: string;
    [ReservedEventColumns.MAPPING_ERRORS]: string;
    [ReservedEventColumns.USER_ID]: string | null;
    [ReservedEventColumns.AUDIENCE_ID]: string;
    [ReservedEventColumns.AUDIENCE_CONTEXT_ID]: string | null;
    [CommonEventColumns.CLIENT_EVENT_ID]: string;
    [CommonEventColumns.CLIENT_TIMESTAMP]: number | null;
    [ReservedCampaignEventColumns.MSG_ID]: string | null;
    [ReservedCampaignEventColumns.STATUS]: string | null;
    [ReservedCampaignEventColumns.CUSTOM_ID]: string | null;
    [ReservedCampaignEventColumns.RECIPENT]: string | null;
    [ReservedCampaignEventColumns.PROVIDER_NAME]: string | null;
    [ReservedCampaignEventColumns.MESSAGE]: string | null;
    [ReservedCampaignEventColumns.REQUEST_TIME]: number | null;
    [ReservedCampaignEventColumns.DELIVERY_TIME]: number | null;
    [ReservedCampaignEventColumns.ACCOUNT_NAME]: string | null;
    [ReservedCampaignEventColumns.CHANNEL]: string | null;
    [ReservedCampaignEventColumns.CAMPAIGN_NAME]: string | null;
    [ReservedCampaignEventColumns.PARENT_CAMPAIGN_ID]: string | null;
    [ReservedCampaignEventColumns.LINK_CLICKED]: string | null;
    [SubscriptionEventColumns.CHANNEL_NAME]: string | null;
    [SubscriptionEventColumns.SUBSCRIPTION_GROUP_ID]: string | null;
    [SubscriptionEventColumns.SUBSCRIPTION_GROUP_TYPE]: string | null;
    [SubscriptionEventColumns.USER_ACTION_TAKEN]: string | null;
    [SubscriptionEventColumns.SUBSCRIBE_TIMESTAMP]: number | null;
    [SubscriptionEventColumns.UNSUBSCRIBE_TIMESTAMP]: number | null;
    [JourneyEventColumns.JOURNEY_ID]: string | null;
    [JourneyEventColumns.JOURNEY_EXECUTION_ID]: string | null;
    [JourneyEventColumns.JOURNEY_NODE_ID]: string | null;
    [JourneyEventColumns.JOURNEY_CONTEXT_ID]: string | null;
    [JourneyEventColumns.JOURNEY_VERSION_ID]: string | null;
    [JourneyEventColumns.JOURNEY_PARENT_ID]: string | null;
    [JourneyEventColumns.JOURNEY_EVENT_METADATA]: string | null;
    [DeliveryControlEventsColumns.DELIVERY_CONTROL_RULE_COUNTER]: number | null; // TODO: remove this column from the table
    [DeliveryControlEventsColumns.DELIVERY_CONTROL_RULE_ID]: string | null; // TODO: remove this column from the table
    [key: string]: string | number | null;
};

export type DbEventLog = {
    [ReservedEventColumns.SOURCE]: string;
    [ReservedEventColumns.RAW_PAYLOAD]: string;
    [ReservedEventColumns.EVENT_ID]: string;
    [ReservedEventColumns.EVENT_NAME]: string;
    [ReservedEventColumns.SERVER_TIMESTAMP]: number;
    [ReservedEventColumns.USER_ID]: string | null;
    [ReservedCampaignEventColumns.CHANNEL]: string | null;
    [ReservedCampaignEventColumns.CAMPAIGN_NAME]: string | null;
    [ReservedCampaignEventColumns.CUSTOM_ID]: string | null;
    [JourneyEventColumns.JOURNEY_ID]: string | null;
    [SubscriptionEventColumns.SUBSCRIPTION_GROUP_ID]: string | null;
    [SubscriptionEventColumns.USER_ACTION_TAKEN]: string | null;
};

export type EventLog = {
    source: string;
    rawPayload: string;
    eventId: string;
    eventName: string;
    serverTimestamp: number;
    userId: string | null;
    journeyId: string | null;
    channel: string;
    campaignId: string | null;
    campaignName: string | null;
    subscriptionId: string | null;
    userActionTaken: string | null;
};

// Added this type to save network bandwidth
export type EventLogWithoutRawPayload = Omit<EventLog, 'rawPayload'>;

export type EventLogsFilters = {
    eventName?: string;
    channel?: string;
    userUuid?: string;
    startDate?: string;
    endDate?: string;
    order?: 'ASC' | 'DESC';
};

export const RequiredReservedColumns: string[] = [
    ReservedEventColumns.USER_ID,
    CommonEventColumns.CLIENT_EVENT_ID,
    CommonEventColumns.CLIENT_TIMESTAMP,
];

export const createEmptyMappedEvent = (): MappedEvent => ({
    [ReservedEventColumns.SOURCE]: '',
    [ReservedEventColumns.RAW_PAYLOAD]: '',
    [ReservedEventColumns.EVENT_ID]: '',
    [ReservedEventColumns.EVENT_NAME]: '',
    [ReservedEventColumns.SERVER_TIMESTAMP]: new Date().getTime(),
    [ReservedEventColumns.EVENT_MAPPING_STATUS]: '',
    [ReservedEventColumns.MAPPING_ERRORS]: '',
    [ReservedEventColumns.USER_ID]: '',
    [ReservedEventColumns.AUDIENCE_ID]: '',
    [ReservedEventColumns.AUDIENCE_CONTEXT_ID]: '',
    [CommonEventColumns.CLIENT_EVENT_ID]: '',
    [CommonEventColumns.CLIENT_TIMESTAMP]: null,
    [ReservedCampaignEventColumns.MSG_ID]: null,
    [ReservedCampaignEventColumns.STATUS]: null,
    [ReservedCampaignEventColumns.CUSTOM_ID]: null,
    [ReservedCampaignEventColumns.RECIPENT]: null,
    [ReservedCampaignEventColumns.PROVIDER_NAME]: null,
    [ReservedCampaignEventColumns.MESSAGE]: null,
    [ReservedCampaignEventColumns.REQUEST_TIME]: null,
    [ReservedCampaignEventColumns.DELIVERY_TIME]: null,
    [ReservedCampaignEventColumns.ACCOUNT_NAME]: null,
    [ReservedCampaignEventColumns.CHANNEL]: null,
    [ReservedCampaignEventColumns.CAMPAIGN_NAME]: null,
    [ReservedCampaignEventColumns.PARENT_CAMPAIGN_ID]: null,
    [SubscriptionEventColumns.CHANNEL_NAME]: '',
    [SubscriptionEventColumns.SUBSCRIPTION_GROUP_ID]: '',
    [SubscriptionEventColumns.SUBSCRIPTION_GROUP_TYPE]: '',
    [SubscriptionEventColumns.USER_ACTION_TAKEN]: '',
    [SubscriptionEventColumns.SUBSCRIBE_TIMESTAMP]: null,
    [SubscriptionEventColumns.UNSUBSCRIBE_TIMESTAMP]: null,
    [JourneyEventColumns.JOURNEY_ID]: null,
    [JourneyEventColumns.JOURNEY_EXECUTION_ID]: null,
    [JourneyEventColumns.JOURNEY_NODE_ID]: null,
    [JourneyEventColumns.JOURNEY_CONTEXT_ID]: null,
    [JourneyEventColumns.JOURNEY_VERSION_ID]: null,
    [JourneyEventColumns.JOURNEY_PARENT_ID]: null,
    [JourneyEventColumns.JOURNEY_EVENT_METADATA]: null,
    [ReservedCampaignEventColumns.LINK_CLICKED]: null,
    [DeliveryControlEventsColumns.DELIVERY_CONTROL_RULE_COUNTER]: null,
    [DeliveryControlEventsColumns.DELIVERY_CONTROL_RULE_ID]: null,
});

export type MappedEventColumn = {
    [key in keyof MappedEvent]: CreateEventColumn;
};
export type EventLogsTableColumn = {
    [key in keyof DbEventLog]: CreateEventColumn;
};

// varchar -> datatype default varchar, string -> datatype varchar(MAX)
export const MappedEventColumnDetails: MappedEventColumn = {
    [ReservedEventColumns.SOURCE]: {
        columnName: ReservedEventColumns.SOURCE,
        columnDataType: DimensionType.STRING,
        nullable: false,
        persistToWh: true,
        label: 'Event Source',
    },
    [ReservedEventColumns.RAW_PAYLOAD]: {
        columnName: ReservedEventColumns.RAW_PAYLOAD,
        columnDataType: DimensionType.STRING,
        nullable: false,
        persistToWh: true,
        label: 'Raw Event Payload',
    },
    [ReservedEventColumns.EVENT_ID]: {
        columnName: ReservedEventColumns.EVENT_ID,
        columnDataType: DimensionType.STRING,
        nullable: false,
        persistToWh: true,
        label: 'Sortment Unique Event ID',
    },
    [ReservedEventColumns.EVENT_NAME]: {
        columnName: ReservedEventColumns.EVENT_NAME,
        columnDataType: DimensionType.STRING,
        nullable: false,
        persistToWh: true,
        label: 'Event Name',
    },
    [ReservedEventColumns.SERVER_TIMESTAMP]: {
        columnName: ReservedEventColumns.SERVER_TIMESTAMP,
        columnDataType: DimensionType.TIMESTAMP,
        nullable: false,
        persistToWh: true,
        label: 'Server Timestamp',
    },
    [ReservedEventColumns.EVENT_MAPPING_STATUS]: {
        columnName: ReservedEventColumns.EVENT_MAPPING_STATUS,
        columnDataType: DimensionType.STRING,
        nullable: false,
        persistToWh: true,
        label: 'Event Mapping Status',
    },
    [ReservedEventColumns.MAPPING_ERRORS]: {
        columnName: ReservedEventColumns.MAPPING_ERRORS,
        columnDataType: DimensionType.STRING,
        nullable: false,
        persistToWh: true,
        label: 'Mapping Errors in case of failure',
    },
    [ReservedEventColumns.USER_ID]: {
        columnName: ReservedEventColumns.USER_ID,
        columnDataType: DimensionType.STRING,
        nullable: false,
        persistToWh: true,
        label: 'User ID',
    },
    [ReservedEventColumns.AUDIENCE_ID]: {
        columnName: ReservedEventColumns.AUDIENCE_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Audience ID',
    },
    [ReservedEventColumns.AUDIENCE_CONTEXT_ID]: {
        columnName: ReservedEventColumns.AUDIENCE_CONTEXT_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Audience Context ID',
    },
    [CommonEventColumns.CLIENT_EVENT_ID]: {
        columnName: CommonEventColumns.CLIENT_EVENT_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Client Event ID',
    },
    [CommonEventColumns.CLIENT_TIMESTAMP]: {
        columnName: CommonEventColumns.CLIENT_TIMESTAMP,
        columnDataType: DimensionType.TIMESTAMP,
        nullable: true,
        persistToWh: true,
        label: 'Client Event Timestamp',
    },
    [ReservedCampaignEventColumns.MSG_ID]: {
        columnName: ReservedCampaignEventColumns.MSG_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Campaign Message ID',
    },
    [ReservedCampaignEventColumns.STATUS]: {
        columnName: ReservedCampaignEventColumns.STATUS,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Campaign Event Status',
    },
    [ReservedCampaignEventColumns.CUSTOM_ID]: {
        columnName: ReservedCampaignEventColumns.CUSTOM_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Custom Identifier',
    },
    [ReservedCampaignEventColumns.RECIPENT]: {
        columnName: ReservedCampaignEventColumns.RECIPENT,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Recipient',
    },
    [ReservedCampaignEventColumns.PROVIDER_NAME]: {
        columnName: ReservedCampaignEventColumns.PROVIDER_NAME,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Provider Name',
    },
    [ReservedCampaignEventColumns.MESSAGE]: {
        columnName: ReservedCampaignEventColumns.MESSAGE,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Campaign Message',
    },
    [ReservedCampaignEventColumns.REQUEST_TIME]: {
        columnName: ReservedCampaignEventColumns.REQUEST_TIME,
        columnDataType: DimensionType.TIMESTAMP,
        nullable: true,
        persistToWh: true,
        label: 'Request Time',
    },
    [ReservedCampaignEventColumns.DELIVERY_TIME]: {
        columnName: ReservedCampaignEventColumns.DELIVERY_TIME,
        columnDataType: DimensionType.TIMESTAMP,
        nullable: true,
        persistToWh: true,
        label: 'Delivery Time',
    },
    [ReservedCampaignEventColumns.ACCOUNT_NAME]: {
        columnName: ReservedCampaignEventColumns.ACCOUNT_NAME,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Account Name',
    },
    [ReservedCampaignEventColumns.CHANNEL]: {
        columnName: ReservedCampaignEventColumns.CHANNEL,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Communication Channel',
    },
    // Leaving campagin name column in WH, as it requires data manipulation on WH to accomodate column removal
    [ReservedCampaignEventColumns.CAMPAIGN_NAME]: {
        columnName: ReservedCampaignEventColumns.CAMPAIGN_NAME,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Campaign Name',
    },
    [ReservedCampaignEventColumns.PARENT_CAMPAIGN_ID]: {
        columnName: ReservedCampaignEventColumns.PARENT_CAMPAIGN_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Parent Campaign ID',
    },
    [ReservedCampaignEventColumns.LINK_CLICKED]: {
        columnName: ReservedCampaignEventColumns.LINK_CLICKED,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Link Clicked',
    },
    [SubscriptionEventColumns.CHANNEL_NAME]: {
        columnName: SubscriptionEventColumns.CHANNEL_NAME,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Channel Name',
    },
    [SubscriptionEventColumns.SUBSCRIPTION_GROUP_ID]: {
        columnName: SubscriptionEventColumns.SUBSCRIPTION_GROUP_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Subscription Group ID',
    },
    [SubscriptionEventColumns.SUBSCRIPTION_GROUP_TYPE]: {
        columnName: SubscriptionEventColumns.SUBSCRIPTION_GROUP_TYPE,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Subscription Group Type',
    },
    [SubscriptionEventColumns.USER_ACTION_TAKEN]: {
        columnName: SubscriptionEventColumns.USER_ACTION_TAKEN,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'User Action Taken',
    },
    [SubscriptionEventColumns.SUBSCRIBE_TIMESTAMP]: {
        columnName: SubscriptionEventColumns.SUBSCRIBE_TIMESTAMP,
        columnDataType: DimensionType.TIMESTAMP,
        nullable: true,
        persistToWh: true,
        label: 'Subscribe Timestamp',
    },
    [SubscriptionEventColumns.UNSUBSCRIBE_TIMESTAMP]: {
        columnName: SubscriptionEventColumns.UNSUBSCRIBE_TIMESTAMP,
        columnDataType: DimensionType.TIMESTAMP,
        nullable: true,
        persistToWh: true,
        label: 'Unsubscribe Timestamp',
    },
    [JourneyEventColumns.JOURNEY_ID]: {
        columnName: JourneyEventColumns.JOURNEY_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Journey ID',
    },
    [JourneyEventColumns.JOURNEY_EXECUTION_ID]: {
        columnName: JourneyEventColumns.JOURNEY_EXECUTION_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Journey Execution ID',
    },
    [JourneyEventColumns.JOURNEY_NODE_ID]: {
        columnName: JourneyEventColumns.JOURNEY_NODE_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Journey Node ID',
    },
    [JourneyEventColumns.JOURNEY_CONTEXT_ID]: {
        columnName: JourneyEventColumns.JOURNEY_CONTEXT_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Journey Context ID',
    },
    [JourneyEventColumns.JOURNEY_VERSION_ID]: {
        columnName: JourneyEventColumns.JOURNEY_VERSION_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Journey Version ID',
    },
    [JourneyEventColumns.JOURNEY_PARENT_ID]: {
        columnName: JourneyEventColumns.JOURNEY_PARENT_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Journey Parent ID',
    },
    [JourneyEventColumns.JOURNEY_EVENT_METADATA]: {
        columnName: JourneyEventColumns.JOURNEY_EVENT_METADATA,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Journey Event Metadata',
    },
    [DeliveryControlEventsColumns.DELIVERY_CONTROL_RULE_COUNTER]: {
        columnName: DeliveryControlEventsColumns.DELIVERY_CONTROL_RULE_COUNTER,
        columnDataType: DimensionType.NUMBER,
        nullable: true,
        persistToWh: true,
        label: 'Delivery Control Rule Counter',
    },
    [DeliveryControlEventsColumns.DELIVERY_CONTROL_RULE_ID]: {
        columnName: DeliveryControlEventsColumns.DELIVERY_CONTROL_RULE_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Delivery Control Rule ID',
    },
};

export const EventLogsColumnDetails: EventLogsTableColumn = {
    [ReservedEventColumns.SOURCE]: {
        columnName: ReservedEventColumns.SOURCE,
        columnDataType: DimensionType.STRING,
        nullable: false,
        persistToWh: true,
        label: 'Event Source',
    },
    [ReservedEventColumns.RAW_PAYLOAD]: {
        columnName: ReservedEventColumns.RAW_PAYLOAD,
        columnDataType: DimensionType.STRING,
        nullable: false,
        persistToWh: true,
        label: 'Raw Event Payload',
    },
    [ReservedEventColumns.EVENT_ID]: {
        columnName: ReservedEventColumns.EVENT_ID,
        columnDataType: DimensionType.STRING,
        nullable: false,
        persistToWh: true,
        label: 'Sortment Unique Event ID',
    },
    [ReservedEventColumns.EVENT_NAME]: {
        columnName: ReservedEventColumns.EVENT_NAME,
        columnDataType: DimensionType.STRING,
        nullable: false,
        persistToWh: true,
        label: 'Event Name',
    },
    [ReservedEventColumns.SERVER_TIMESTAMP]: {
        columnName: ReservedEventColumns.SERVER_TIMESTAMP,
        columnDataType: DimensionType.TIMESTAMP,
        nullable: false,
        persistToWh: true,
        label: 'Server Timestamp',
    },
    [ReservedEventColumns.USER_ID]: {
        columnName: ReservedEventColumns.USER_ID,
        columnDataType: DimensionType.STRING,
        nullable: false,
        persistToWh: true,
        label: 'User ID',
    },
    [ReservedCampaignEventColumns.CHANNEL]: {
        columnName: ReservedCampaignEventColumns.CHANNEL,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Communication Channel',
    },
    [ReservedCampaignEventColumns.CUSTOM_ID]: {
        columnName: ReservedCampaignEventColumns.CUSTOM_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Custom Identifier',
    },
    [ReservedCampaignEventColumns.CAMPAIGN_NAME]: {
        columnName: ReservedCampaignEventColumns.CAMPAIGN_NAME,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Campaign Name',
    },
    [JourneyEventColumns.JOURNEY_ID]: {
        columnName: JourneyEventColumns.JOURNEY_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Journey ID',
    },
    [SubscriptionEventColumns.SUBSCRIPTION_GROUP_ID]: {
        columnName: SubscriptionEventColumns.SUBSCRIPTION_GROUP_ID,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'Subscription Group ID',
    },
    [SubscriptionEventColumns.USER_ACTION_TAKEN]: {
        columnName: SubscriptionEventColumns.USER_ACTION_TAKEN,
        columnDataType: DimensionType.STRING,
        nullable: true,
        persistToWh: true,
        label: 'User Action Taken',
    },
};

export enum QueueType {
    SQLITE = 'SQLITE',
    KAFKA = 'KAFKA',
}

export enum STATUSES {
    SUCCESS = 'SUCCESS',
    FAILED = 'FAILED',
}

export enum MapperType {
    VALUE = 'VALUE',
    CONCAT = 'CONCAT',
    CUSTOM = 'CUSTOM',
}

export type MapperConfig = ValueConfig | ConcatConfig | CustomConfig;

export type ValueConfig = {
    path: string;
    type: MapperType.VALUE;
};

export type ConcatConfig = {
    path: string[];
    delimeter: string;
    type: MapperType.CONCAT;
};

export type CustomConfig = {
    path: string[];
    expression: string | null;
    type: MapperType.CUSTOM;
};

export type DateTimeFieldConfig = {
    hasTimezone: boolean;
    // Below field is required in cases where hasTimezone is false
    offset: string | null;
    type: FieldConfig.TIMESTAMPTZ;
};
export type EpochFieldConfig = {
    isEpoch: boolean;
    type: FieldConfig.EPOCH;
};

export type MapperSchema = {
    required: boolean;
    mapperType: MapperType;
    mapperConfig: MapperConfig;
    fieldConfig: DateTimeFieldConfig | EpochFieldConfig | null;
    dataType: DimensionType;
    isUrlEncoded: boolean;
};

export type RawEvent = {
    projectUuid: string;
    [ReservedEventColumns.SOURCE]: string;
    [key: string]: AnyType;
};

export type SourceEventKeyMapping = {
    uuid: string;
    projectUuid: string;
    source: string;
    eventNameKeys: Array<string>;
    description: string;
    createdAt: Date;
    createdBy: string | null;
    isInternal: boolean;
};

export type UpdateSourceEventKeyMappings = Partial<
    Pick<SourceEventKeyMapping, 'eventNameKeys' | 'description'>
>;

export type RunCustomMaterialisationPayload = {
    endDate: string;
};

/**
 *
 * Below is the structure like {
 *   projectUuid:{
 *       gupshup:{
 *           event_delivered:{
 *               MapperSChema
 *           }
 *       }
 *   }
 * }
 */
export type EventMapperSchema = {
    uuid: string;
    projectUuid: string;
    source: string;
    eventName: string;
    mapperSchema: Record<string, MapperSchema>;
    sampleEventPayload: Record<string, unknown>;
    label: string | null;
    createdBy: string | null;
    createdAt: Date | null;
    updatedBy: string | null;
    updatedAt: Date | null;
    persistToWh: boolean;
    isInternal: boolean;
    tags: string[] | undefined;
};

export type CreateSourceEventNameKeyMapping = Pick<
    SourceEventKeyMapping,
    'source' | 'eventNameKeys' | 'description'
>;
export type CreateSourceEventNameKeyMappingResponse = SourceEventKeyMapping;

export type CreateEventMapperSchema = Omit<
    EventMapperSchema,
    | 'uuid'
    | 'projectUuid'
    | 'createdBy'
    | 'createdAt'
    | 'updatedAt'
    | 'updatedBy'
>;

export type UpdateEventMapperSchema = Pick<
    EventMapperSchema,
    | 'eventName'
    | 'mapperSchema'
    | 'sampleEventPayload'
    | 'label'
    | 'persistToWh'
    | 'isInternal'
    | 'tags'
>;

export type CachedMappers = Record<
    string,
    Record<string, Record<string, Record<string, MapperSchema>>>
>;

export type EventColumn = {
    uuid: UUID;
    projectUuid: UUID;
    columnName: string;
    columnDataType: DimensionType;
    nullable: boolean;
    label: string;
    persistToWh: boolean;
};

export type CreateEventColumn = Omit<EventColumn, 'uuid' | 'projectUuid'>;

export type CreateEventColumnFromLabel = Omit<
    EventColumn,
    'uuid' | 'projectUuid' | 'columnName' | 'persistToWh'
>;

export enum FieldConfig {
    TIMESTAMPTZ = 'TIMESTAMPTZ',
    EPOCH = 'EPOCH',
}

export enum PSQLDimensionTypeColumnInfo {
    TIMESTAMP = 'timestamp without time zone',
}

export type PartitionConfig = {
    column: ReservedEventColumns;
    granularity: TimeFrames;
};

export type ColumnInfo = {
    type: string; // TODO these types which are based on DB(psql can send character varying, other can send
    // string or text etc), map these to DimensionType types
    maxLength: number;
    nullable: boolean;
    defaultValue: string | null;
};

export type DirectTableFailureEntry = {
    count: number;
    entry: MappedEvent;
};

export enum ParquetDataType {
    BOOLEAN = 'BOOLEAN',
    UTF8 = 'UTF8',
    INT32 = 'INT32',
    INT64 = 'INT64',
    TIMESTAMP_MILLIS = 'TIMESTAMP_MILLIS',
    FLOAT = 'FLOAT',
    DOUBLE = 'DOUBLE',
}

export const dimensionTypeToParquetDataType = (
    dimensionType: DimensionType,
): ParquetDataType => {
    switch (dimensionType) {
        case DimensionType.BOOLEAN:
            return ParquetDataType.BOOLEAN;
        case DimensionType.DATE:
            return ParquetDataType.INT32;
        case DimensionType.STRING:
            return ParquetDataType.UTF8;
        case DimensionType.TIMESTAMP:
            return ParquetDataType.TIMESTAMP_MILLIS;
        case DimensionType.NUMBER:
            return ParquetDataType.DOUBLE;
        default:
            return ParquetDataType.UTF8;
    }
};

export const CampaignAnalyticsRequiredCols: string[] = [
    ...Object.values(ReservedCampaignEventColumns),
    ...Object.values(CommonEventColumns),
    ...Object.values(ReservedEventColumns),
    ...Object.values(SubscriptionEventColumns),
];

export const EventLogsRequiredCols: string[] = [
    ReservedEventColumns.SOURCE,
    ReservedEventColumns.EVENT_NAME,
    ReservedEventColumns.RAW_PAYLOAD,
    ReservedEventColumns.EVENT_ID,
    ReservedEventColumns.SERVER_TIMESTAMP,
    ReservedEventColumns.USER_ID,
    ReservedCampaignEventColumns.CHANNEL,
    ReservedCampaignEventColumns.CAMPAIGN_NAME,
    ReservedCampaignEventColumns.CUSTOM_ID,
    JourneyEventColumns.JOURNEY_ID,
    SubscriptionEventColumns.USER_ACTION_TAKEN,
    SubscriptionEventColumns.SUBSCRIPTION_GROUP_ID,
];

export const BotDetectionRequiredCols: string[] = [
    ReservedEventColumns.USER_ID,
    CommonEventColumns.CLIENT_EVENT_ID,
];

export type EventLogsWHStats = {
    source: string;
    eventName: string;
    count: {
        last30: number;
        last7: number;
    };
    lastReceivedAt: string;
};

export type EventLogsDbStats = Pick<
    EventMapperSchema,
    | 'createdAt'
    | 'updatedAt'
    | 'createdBy'
    | 'updatedBy'
    | 'label'
    | 'uuid'
    | 'isInternal'
    | 'tags'
    | 'sampleEventPayload'
>;

export type EventLogsStats = EventLogsWHStats & EventLogsDbStats;

export enum EventQueryParams {
    NAME = 'name',
    STATS = 'stats',
    VIEW_ID = 'viewId',
    FILTERS = 'filters',
    SORT = 'sort',
    ORDER = 'order',
    INCLUDES_TAGS = 'includesTags',
    EXCLUDES_TAGS = 'excludesTags',
}

export type EventLogsStatsList = {
    data: EventLogsStats[];
    paginate: {
        total?: number;
        lastPage?: number;
        currentPage: number;
        perPage: number;
        from: number;
        to: number;
    };
};

export const SubscriptionEventsRequiredCols: string[] = [
    ReservedEventColumns.SOURCE,
    ReservedEventColumns.EVENT_NAME,
    CommonEventColumns.CLIENT_EVENT_ID,
    ReservedEventColumns.USER_ID,
    ReservedEventColumns.SERVER_TIMESTAMP,
    SubscriptionEventColumns.CHANNEL_NAME,
    SubscriptionEventColumns.SUBSCRIPTION_GROUP_ID,
    SubscriptionEventColumns.SUBSCRIPTION_GROUP_TYPE,
    SubscriptionEventColumns.USER_ACTION_TAKEN,
    SubscriptionEventColumns.SUBSCRIBE_TIMESTAMP,
    SubscriptionEventColumns.UNSUBSCRIBE_TIMESTAMP,
    ReservedCampaignEventColumns.CUSTOM_ID,
    ReservedCampaignEventColumns.RECIPENT,
];

export const JourneyEventsRequiredCols: string[] = [
    ReservedEventColumns.SOURCE,
    ReservedEventColumns.EVENT_NAME,
    CommonEventColumns.CLIENT_EVENT_ID,
    CommonEventColumns.CLIENT_TIMESTAMP,
    ReservedEventColumns.USER_ID,
    ReservedEventColumns.SERVER_TIMESTAMP,
    JourneyEventColumns.JOURNEY_ID,
    JourneyEventColumns.JOURNEY_EXECUTION_ID,
    JourneyEventColumns.JOURNEY_NODE_ID,
    JourneyEventColumns.JOURNEY_CONTEXT_ID,
    JourneyEventColumns.JOURNEY_VERSION_ID,
    JourneyEventColumns.JOURNEY_PARENT_ID,
    JourneyEventColumns.JOURNEY_EVENT_METADATA,
];

export const CustomerCommunicationLogsRequiredCols: string[] = [
    ReservedEventColumns.SOURCE,
    ReservedEventColumns.EVENT_NAME,
    CommonEventColumns.CLIENT_EVENT_ID,
    ReservedEventColumns.USER_ID,
    ReservedCampaignEventColumns.CHANNEL,
    ReservedCampaignEventColumns.RECIPENT,
    CommonEventColumns.CLIENT_TIMESTAMP,
];

export enum FynoEventNames {
    SUCCESS = 'Success',
    REJECTED = 'REJECTED',
    PROCESSED = 'PROCESSED',
    OPEN = 'OPEN',
    DELIVERED = 'DELIVERED',
    CLICKED = 'CLICKED',
    READ = 'READ',
    SENT = 'Sent',
    BOUNCE = 'BOUNCE',
    FAILED = 'FAILED',
    UNDELIVERED = 'UNDELIVERED',
    REPLIED = 'REPLIED',
    SPAM = 'SPAMREPORT',
    DENIED = 'DENIED',
}

export type UpdateEventTagsPayload = {
    tags: string[];
};

export enum TrackingEventName {
    SAVE_PREFERENCE_CLICKED = 'save_preference_clicked',
    GLOBAL_PREFERENCE_UPDATE_CLICKED = 'global_preference_update_clicked',
    UNSUBSCRIBE_BUTTON_CLICKED = 'unsubscribe_button_clicked',
}

export type TrackingEventRequestPayload = {
    eventName: TrackingEventName;
    payload: Record<string, AnyType>;
    timestamp: string;
    userAgent: string;
    url: string;
    referrer: string;
};

export const AllowedJourneyBusinessEventSignals = [USER_WH_CACHE_SYNC_EVENT];
